import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Order, Dashboard } from "@apis/index";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helper } from "@utils/index";
import { Calendar } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

const statusConfig: any = {
  "Order Placed": {
    label: "Order Placed",
    color: "#92400e",
    backgroundColor: "#fef3c7",
    icon: "📝",
    path: "order-placed",
  },
  "Order Confirmed": {
    label: "Order Confirmed",
    color: "#065f46",
    backgroundColor: "#d1fae5",
    icon: "✅",
    path: "order-confirmed",
  },
  "Order Processing": {
    label: "Order Processing",
    color: "#1e40af",
    backgroundColor: "#dbeafe",
    icon: "📦",
    path: "order-processing",
  },
  "Delivery Assigned": {
    label: "Delivery Assigned",
    color: "#7c3aed",
    backgroundColor: "#ede9fe",
    icon: "🚴",
    path: "delivery-assigned",
  },
  "Ready for Pickup": {
    label: "Ready for Pickup",
    color: "#0891b2",
    backgroundColor: "#cffafe",
    icon: "📋",
    path: "ready-for-pickup",
  },
  "Order Picked Up": {
    label: "Order Picked Up",
    color: "#ea580c",
    backgroundColor: "#ffedd5",
    icon: "🥡",
    path: "order-picked-up",
  },
  "Out for Delivery": {
    label: "Out for Delivery",
    color: "#7c2d12",
    backgroundColor: "#fed7aa",
    icon: "🚚",
    path: "out-for-delivery",
  },
  "Near Customer": {
    label: "Near Customer",
    color: "#15803d",
    backgroundColor: "#bbf7d0",
    icon: "📍",
    path: "near-customer",
  },
  Delivered: {
    label: "Delivered",
    color: "#047857",
    backgroundColor: "#dcfce7",
    icon: "✓",
    path: "delivered",
  },
  Cancelled: {
    label: "Cancelled",
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    icon: "❌",
    path: "cancelled",
  },
  Rejected: {
    label: "Rejected",
    color: "#9f1239",
    backgroundColor: "#ffe4e6",
    icon: "🚫",
    path: "rejected",
  },
  "Prescription Requested": {
    label: "Prescription Requested",
    color: "#00695c",
    backgroundColor: "#e0f2f1",
    icon: "📄",
    path: "prescription-requested",
  },
  "Prescription Accepted": {
    label: "Prescription Accepted",
    color: "#0f766e",
    backgroundColor: "#ccfbf1",
    icon: "👍",
    path: "prescription-accepted",
  },
  "Creating Invoice": {
    label: "Creating Invoice",
    color: "#9d174d",
    backgroundColor: "#fce7f3",
    icon: "🧾",
    path: "creating-invoice",
  },
  "Invoice Shared": {
    label: "Invoice Shared",
    color: "#6a1b9a",
    backgroundColor: "#f3e5f5",
    icon: "📤",
    path: "invoice-shared",
  },
  "Invoice Accepted": {
    label: "Invoice Accepted",
    color: "#166534",
    backgroundColor: "#dcfce7",
    icon: "✅",
    path: "invoice-accepted",
  },
  "Prescription Rejected": {
    label: "Prescription Rejected",
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    icon: "👎",
    path: "prescription-rejected",
  },
};

// ... existing code ...
// ... existing code ...
const OrderStatusCards = ({
  storeId,
  customerId,
  deliveryBoyId,
  moduleId,
  zoneId,
  startDate,
  endDate,
}: {
  storeId?: number | string;
  customerId?: string;
  deliveryBoyId?: string;
  moduleId?: number;
  zoneId?: number | string;
  startDate?: string;
  endDate?: string;
}) => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = Helper.getUser();

  const { data: modules } = useSelector((state: RootState) => state.modules);

  const currentModuleId = moduleId || Number(searchParams.get("moduleId"));
  const currentModuleName = searchParams.get("module");

  const selectedModule = modules?.find((m: any) => {
    if (currentModuleId) return m.id === currentModuleId;
    if (currentModuleName) return m.name.toLowerCase() === currentModuleName.toLowerCase();
    return false;
  });

  const isPharma = selectedModule ? selectedModule.type === "pharma" : true;

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const params: any = { startDate, endDate };
        if (storeId && storeId !== 0) params.storeId = storeId;
        if (customerId) params.createdBy = customerId;
        if (deliveryBoyId) params.deliveryBoyId = deliveryBoyId;
        if (moduleId) params.moduleId = moduleId;
        if (zoneId && zoneId !== "all") params.zoneId = zoneId;

        const res: any = await Dashboard.orderStatusStats({ params });
        if (res) {
          setCounts(res);
        }
      } catch (error) {
        console.error("Failed to fetch order status stats", error);
      }
    };

    fetchCounts();
  }, [startDate, endDate, moduleId, zoneId, storeId]);

  const filteredStatuses = Object.entries(statusConfig).filter(([status]) => {
    const isPharmaStatus = [
      "Prescription Requested",
      "Prescription Accepted",
      "Creating Invoice",
      "Invoice Shared",
      "Invoice Accepted",
      "Prescription Rejected"
    ].includes(status);

    if (isPharmaStatus) {
      return isPharma;
    }
    return true;
  });

  return (
    <div>
      <Header>
        <Title>Order Overview</Title>
      </Header>
      <CardsContainer>
        {filteredStatuses.map(([status, config]: [string, any]) => (
          <Card
            key={status}
            onClick={() => navigate(`/${user?.role}/orders/${config.path}`)}
          >
            <IconWrapper style={{ background: config.backgroundColor }}>
              <span style={{ fontSize: "20px" }}>{config.icon}</span>
            </IconWrapper>
            <CardContent>
              <CardTitle>{config.label}</CardTitle>
              <CardCount>{counts[status] || 0}</CardCount>
            </CardContent>
          </Card>
        ))}
      </CardsContainer>
    </div>
  );
};

// Styled Components
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const FilterTabsContainer = styled.div`
  display: flex;
  background-color: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
`;

const FilterTab = styled.button<{ isActive: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  background-color: ${(props) => (props.isActive ? "white" : "transparent")};
  color: ${(props) => (props.isActive ? "#0f172a" : "#64748b")};
  box-shadow: ${(props) =>
    props.isActive ? "0 1px 2px rgba(0,0,0,0.1)" : "none"};

  &:hover {
    color: #0f172a;
    background-color: ${(props) => (props.isActive ? "white" : "#e2e8f0")};
  }
`;

const DateInputsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
`;

const StyledDateInput = styled.input`
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #3b82f6;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;
// ... existing Card, IconWrapper, CardContent, CardTitle, CardCount ...

const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
`;

const CardCount = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
`;

export default OrderStatusCards;
